import {formatDate, objectById, priorityLabel, getUserFullName, objectBy} from './utils'
import {ptracPost} from './ptracApi'
import {OK} from 'http-status-codes'
import _ from 'lodash'

export function hydrateTaskSearch(tasks, taskTemplates, users, loadingProgress) {
  const hydrate = async (tasks, taskTemplates, users) => {
    loadingProgress.percent = 30
    loadingProgress.loaded = Math.round(loadingProgress.total * loadingProgress.percent/100)

    // allow hydrateTaskSearch to work for both Elasticsearch results and ptrac-api results.
    const isElasticsearchResult = !!tasks[0]._source

    const projectMap = await getProjects(tasks, isElasticsearchResult ? '_source.projectId' : 'projectId')
    const jobMap = await getJobs(tasks, isElasticsearchResult ? '_source.jobId' : 'jobId')

    loadingProgress.percent = 65
    loadingProgress.loaded = Math.round(loadingProgress.total * loadingProgress.percent/100)

    const jobs = Array.from(jobMap.values())
    const companyMap = await getCompanies(jobs, 'clientId', 'builderId')
    const planNameMap = await getPlanNames(jobs, 'planNameId')
    const fileMap = await getFiles(planNameMap, 'fileId')

    loadingProgress.percent = 85
    loadingProgress.loaded = Math.round(loadingProgress.total * loadingProgress.percent/100)

    const taskMap = new Map()
    for (const task of tasks) {
      taskMap.set(task._id, task)
    }

    const taskTemplateMap = new Map()
    for (const taskTemplate of taskTemplates) {
      taskTemplateMap.set(taskTemplate.id, taskTemplate)
    }

    const userMap = new Map()
    for (const user of users) {
      userMap.set(user.id, user)
    }

    for (const task of tasks) {
      const sourceTask = isElasticsearchResult ? task._source : task
      sourceTask.orphaned = false
      sourceTask.id = isElasticsearchResult ? task._id : task.id

      if (sourceTask.linkedTaskId) {
        if (isElasticsearchResult) {
          const linkedTaskES = taskMap.get(sourceTask.linkedTaskId)
          if (linkedTaskES) {
            sourceTask.linkedTask = JSON.parse(JSON.stringify(linkedTaskES._source))
            sourceTask.linkedTask.id = linkedTaskES._id
          } else {
            sourceTask.linkedTask = {
              id: null,
              name: 'Not available in search results'
            }
          }
        } else {
          const linkedTaskPtrac = taskMap.get(sourceTask.linkedTaskId)
          if (linkedTaskPtrac) {
            sourceTask.linkedTask = JSON.parse(JSON.stringify(linkedTaskPtrac))
            sourceTask.linkedTask.id = linkedTaskPtrac.id
          } else {
            sourceTask.linkedTask = {
              id: null,
              name: 'Not available in search results'
            }
          }
        }

      }

      sourceTask.taskTemplate = taskTemplateMap.get(sourceTask.templateId)

      sourceTask.dueDateFormatted = null
      if (sourceTask.dueDate) {
        sourceTask.dueDateFormatted = formatDate(sourceTask.dueDate)
      }
      sourceTask.completionDateFormatted = null
      if (sourceTask.completionDate) {
        sourceTask.completionDateFormatted = formatDate(sourceTask.completionDate)
      }
      if (sourceTask.priority) {
        sourceTask.priorityLabel = priorityLabel(sourceTask.priority)
        sourceTask.priority = +sourceTask.priority
      }
      const assignee = userMap.get(sourceTask.assigneeId)
      sourceTask.assignee = ''
      if (assignee) {
        sourceTask.assignee = getUserFullName(assignee)
      }
      const owner = userMap.get(sourceTask.ownerId)
      sourceTask.owner = ''
      if (owner) {
        sourceTask.owner = getUserFullName(owner)
      }

      sourceTask.project = projectMap.get(sourceTask.projectId)

      if (!sourceTask.project) {
        sourceTask.orphaned = true
        sourceTask.project = { name: 'ORPHANED' }
        continue
      }

      sourceTask.job = jobMap.get(sourceTask.jobId)

      if (!sourceTask.job) {
        sourceTask.orphaned = true
        sourceTask.job = { name: 'ORPHANED' }
        continue
      }

      sourceTask.job.client = companyMap.get(sourceTask.job.clientId)
      sourceTask.job.builder = companyMap.get(sourceTask.job.builderId)

      if (sourceTask.job.priority) {
        sourceTask.job.priorityLabel = priorityLabel(sourceTask.job.priority)
        sourceTask.job.priority = +sourceTask.job.priority // turn to number so it can be sorted
      }
      const coordinator = userMap.get(sourceTask.job.coordinatorId)
      sourceTask.job.coordinator = ''
      if (coordinator) {
        sourceTask.job.coordinator = getUserFullName(coordinator)
      }

      if (sourceTask.job.planNameId && planNameMap.length > 0) {
        sourceTask.job.planName = planNameMap.get(sourceTask.job.planNameId)
        if (sourceTask.job.planName && sourceTask.job.planName.fileId) {
          if (fileMap.length > 0) {
            sourceTask.job.planName.file = fileMap.get(sourceTask.job.planName.fileId)
          }
        }
      }
    }

    return tasks
  }

  return hydrate(tasks, taskTemplates, users)
}

export function hydrateTaskSearch2(allTasks, taskTemplates, users, projects, jobs, companies, planNames, files) {
  const hydrate = async (allTasks, taskTemplates, users, projects, jobs, companies, planNames, files) => {
    // const projects = await getProjects(tasksChunk, '_source.projectId')
    // const jobs = await getJobs(tasksChunk, '_source.jobId')
    // const companies = await getCompanies(jobs, 'clientId', 'builderId')
    // const planNames = await getPlanNames(jobs, 'planNameId')
    // const files = await getFiles(planNames, 'fileId')

    for (const taskResult of allTasks) {
      const sourceTask = taskResult._source
      sourceTask.orphaned = false
      sourceTask.id = taskResult._id

      // if (sourceTask.linkedTaskId) {
      //   const linkedTask = objectBy(allTasks, '_id', sourceTask.linkedTaskId)
      //   if (linkedTask) {
      //     sourceTask.linkedTask = JSON.parse(JSON.stringify(linkedTask._source))
      //     sourceTask.linkedTask.id = linkedTask._id
      //   } else {
      //     sourceTask.linkedTask = {
      //       id: null,
      //       name: 'Not available in search results'
      //     }
      //   }
      // }

      sourceTask.taskTemplate = objectById(taskTemplates, sourceTask.templateId)

      sourceTask.dueDateFormatted = null
      if (sourceTask.dueDate) {
        sourceTask.dueDateFormatted = formatDate(sourceTask.dueDate)
      }
      sourceTask.completionDateFormatted = null
      if (sourceTask.completionDate) {
        sourceTask.completionDateFormatted = formatDate(sourceTask.completionDate)
      }
      if (sourceTask.priority) {
        sourceTask.priorityLabel = priorityLabel(sourceTask.priority)
        sourceTask.priority = +sourceTask.priority
      }
      const assignee = objectById(users, sourceTask.assigneeId)
      sourceTask.assignee = ''
      if (assignee) {
        sourceTask.assignee = getUserFullName(assignee)
      }
      const owner = objectById(users, sourceTask.ownerId)
      sourceTask.owner = ''
      if (owner) {
        sourceTask.owner = getUserFullName(owner)
      }

      sourceTask.project = objectById(projects, sourceTask.projectId)

      if (!sourceTask.project) {
        sourceTask.orphaned = true
        sourceTask.project = { name: 'ORPHANED' }
        continue
      }

      sourceTask.job = objectById(jobs, sourceTask.jobId)

      if (!sourceTask.job) {
        sourceTask.orphaned = true
        sourceTask.job = { name: 'ORPHANED' }
        continue
      }

      sourceTask.job.client = objectById(companies, sourceTask.job.clientId)
      sourceTask.job.builder = objectById(companies, sourceTask.job.builderId)

      if (sourceTask.job.priority) {
        sourceTask.job.priorityLabel = priorityLabel(sourceTask.job.priority)
        sourceTask.job.priority = +sourceTask.job.priority
      }
      const coordinator = objectById(users, sourceTask.job.coordinatorId)
      sourceTask.job.coordinator = ''
      if (coordinator) {
        sourceTask.job.coordinator = getUserFullName(coordinator)
      }

      if (sourceTask.job.planNameId && planNames.length > 0) {
        sourceTask.job.planName = objectById(planNames, sourceTask.job.planNameId)
        if (sourceTask.job.planName && sourceTask.job.planName.fileId) {
          if (files.length > 0) {
            sourceTask.job.planName.file = objectById(files, sourceTask.job.planName.fileId)
          }
        }
      }
    }

    return allTasks
  }

  return hydrate(allTasks, taskTemplates, users, projects, jobs, companies, planNames, files)
}

export function getLegalSubdivisions(projects, mapPath) {
  const legalSubdivisions = async (projects, mapPath) => {
    const legalSubdivisionIds = _.uniq(_.compact(_.map(projects, mapPath)))
    return await ptracPost('legal-subdivisions/findByIdIn', legalSubdivisionIds)
  }

  const response = legalSubdivisions(projects, mapPath)

  const map = new Map()
  if (OK === response.status) {
    for (const legalSubdivision of response.data) {
      map.set(legalSubdivision.id, legalSubdivision)
    }
  }

  return map
}

export function getProjects(tasks, mapPath) {
  const projects = async (tasks, mapPath) => {
    const projectIds = _.uniq(_.map(tasks, mapPath))
    const response = await ptracPost('projects/findByIdIn', projectIds)
    const map = new Map()
    if (OK === response.status) {
      for (const project of response.data) {
        map.set(project.id, project)
      }
    }
    return map
  }

  return projects(tasks, mapPath)
}

export function getJobs(tasks, mapPath) {
  const jobs = async (tasks, mapPath) => {
    const jobIds = _.uniq(_.map(tasks, mapPath))
    const response = await ptracPost('jobs/findByIdIn', jobIds)
    const map = new Map()
    if (OK === response.status) {
      for (const job of response.data) {
        map.set(job.id, job)
      }
    }
    return map
  }

  return jobs(tasks, mapPath)
}

export function getLinkedTasks(esTasks, mapPath) {
  const tasks = async (esTasks, mapPath) => {
    const taskIds = _.uniq(_.map(esTasks, mapPath))
    const response = await ptracPost('tasks/findByIdIn', taskIds)
    const map = new Map()
    if (OK === response.status) {
      for (const task of response.data) {
        map.set(task.id, task)
      }
    }
    return map
  }

  return tasks(esTasks, mapPath)
}

export function getLinkedTasksByIds(taskIds) {
  const tasks = async (taskIds) => {
    const response = await ptracPost('tasks/findByIdIn', _.uniq(taskIds))
    const map = new Map()
    if (OK === response.status) {
      for (const task of response.data) {
        map.set(task.id, task)
      }
    }
    return map
  }

  return tasks(taskIds)
}


export function getCompanies(jobs, mapPathClients, mapPathBuilders) {
  const companies = async (jobs, mapPathClients, mapPathBuilders) => {
    const clientIds = _.map(jobs, mapPathClients)
    const builderIds = _.map(jobs, mapPathBuilders)
    const allCompanyIds = _.uniq(_.compact(clientIds.concat(builderIds)))

    const map = new Map()

    if (allCompanyIds) {
      const response = await ptracPost('companies/findByIdIn', allCompanyIds)
      if (OK === response.status) {
        for (const company of response.data) {
          map.set(company.id, company)
        }
      }
    }

    return map
  }

  return companies(jobs, mapPathClients, mapPathBuilders)
}

export function getPlanNames(jobs, mapPath) {
  const planNames = async (jobs, mapPath) => {
    const planNameIds = _.uniq(_.compact(_.map(jobs, mapPath)))
    const map = new Map()
    if (planNameIds && planNameIds.length > 0) {
      const response = await ptracPost('plan-names/findByIdIn', planNameIds)
      if (OK === response.status) {
        for (const planName of response.data) {
          map.set(planName.id, planName)
        }
      }
    }

    return map
  }

  return planNames(jobs, mapPath)
}

export function getFiles(planNames, mapPath) {
  const files = async (planNames, mapPath) => {
    const fileIds = _.uniq(_.map(planNames, mapPath))

    const map = new Map()

    if (fileIds && fileIds.length > 0) {
      const response = await ptracPost('files/findByIdIn', fileIds)
      if (OK === response.status) {
        for (const file of response.data) {
          map.set(file.id, file)
        }
      }
    }

    return map
  }

  return files(planNames, mapPath)
}

export function hydratePriceRule(priceRule) {
  if (priceRule.client || priceRule.clientId) {
    priceRule.client = {name: priceRule.client || null, id: priceRule.clientId || null}
    delete priceRule.clientId
  }
  if (priceRule.builderId || priceRule.builder) {
    priceRule.builder = {name: priceRule.builder || null, id: priceRule.builderId || null}
    delete priceRule.builderId
  }
  if (priceRule.taskName || priceRule.taskNameId) {
    priceRule.taskName = {name: priceRule.taskName || null, id: priceRule.taskNameId || null}
    delete priceRule.taskNameId
  }
  if (priceRule.community || priceRule.communityId) {
    priceRule.community = {name: priceRule.community || null, id: priceRule.communityId || null}
    delete priceRule.communityId
  }

  return priceRule
}
